import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sun, Moon, Volume2, VolumeX, LogOut, Zap, Trophy, History, ExternalLink, Sparkles, Share2, Flame, RefreshCw, Award, Gift, Users } from 'lucide-react'
import confetti from 'canvas-confetti'
import { useStore, PrizeType, SpinResult } from './store'
import { spinWheel, getBalance, getEligibility, sanitizeErrorMessage, EligibilityData } from './api'
import PrizeWheel from './components/PrizeWheel'
import LoginPage from './components/LoginPage'
import ParticleBackground from './components/ParticleBackground'
import ToastContainer, { showToast } from './components/Toast'
import ErrorBoundary from './components/ErrorBoundary'
import ConnectionIndicator from './components/ConnectionIndicator'
import LoadingScreen from './components/LoadingScreen'
import CursorGlow from './components/CursorGlow'
import ExternalLinks from './components/ExternalLinks'
import { playSpinStart, playWin, playLose, playClick } from './lib/sounds'

const SPIN_COST = 1000
const SESSION_TIMEOUT = 30 * 60 * 1000
const BALANCE_REFRESH_INTERVAL = 60_000

function prizeLabel(p: PrizeType): string {
  const map: Record<PrizeType, string> = {
    good_luck_next_time: 'Try Again',
    free_spin: 'Free Spin!',
    usdt5: '5 USDT!',
    usdt100: '100 USDT!',
    iphone17: 'iPhone 17!',
  }
  return map[p] || ''
}

function prizeEmoji(p: PrizeType): string {
  const map: Record<PrizeType, string> = {
    good_luck_next_time: '',
    free_spin: '',
    usdt5: '',
    usdt100: '',
    iphone17: '',
  }
  return map[p] || ''
}

function isWin(p: PrizeType) { return p !== 'good_luck_next_time' }

function useAnimatedNumber(target: number, duration = 600) {
  const [display, setDisplay] = useState(target)
  const prevRef = useRef(target)
  useEffect(() => {
    const start = prevRef.current
    if (start === target) return
    const diff = target - start
    const t0 = performance.now()
    let frame: number
    const tick = (now: number) => {
      const p = Math.min((now - t0) / duration, 1)
      const e = 1 - Math.pow(1 - p, 3)
      setDisplay(Math.round(start + diff * e))
      if (p < 1) frame = requestAnimationFrame(tick)
      else prevRef.current = target
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, duration])
  return display
}

function fireConfetti() {
  const colors = ['#FF6B00', '#FF8533', '#1A1A1A', '#FF9F4D']
  confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors, gravity: 0.8, scalar: 1.2 })
  setTimeout(() => {
    confetti({ particleCount: 40, spread: 100, origin: { y: 0.5, x: 0.3 }, colors })
    confetti({ particleCount: 40, spread: 100, origin: { y: 0.5, x: 0.7 }, colors })
  }, 300)
}

// Weighted random segment index for test mode
const TEST_WEIGHTS = [25, 5, 25, 5, 25, 5, 1, 5, 0.5, 1] // TRY AGAIN heavy, prizes rare
function pickTestSegment(): number {
  const total = TEST_WEIGHTS.reduce((a, b) => a + b, 0)
  let r = Math.random() * total
  for (let i = 0; i < TEST_WEIGHTS.length; i++) {
    r -= TEST_WEIGHTS[i]
    if (r <= 0) return i
  }
  return 0
}

const PRIZE_TYPE_MAP: PrizeType[] = [
  'good_luck_next_time', 'free_spin', 'good_luck_next_time', 'usdt5',
  'good_luck_next_time', 'free_spin', 'usdt100', 'usdt5',
  'iphone17', 'usdt100',
]

const ADMIN_EMAIL = 'annedabest@gmail.com'

function WheelPage() {
  const store = useStore()
  const { user, isSpinning, lastSpinResult, spins, soundEnabled, theme } = store
  const [showResult, setShowResult] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [screenFlash, setScreenFlash] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [eligibility, setEligibility] = useState<EligibilityData | null>(null)
  const [testMode, setTestMode] = useState(false)
  const pullStartY = useRef(0)
  const pullDelta = useRef(0)

  const isAdmin = user?.email === ADMIN_EMAIL
  const canSpin = !!user && !isSpinning && user.balance >= SPIN_COST
  const animatedBalance = useAnimatedNumber(user?.balance ?? 0)

  // Win streak calculation
  let winStreak = 0
  for (const s of spins) {
    if (isWin(s.prize_type)) winStreak++
    else break
  }

  // Keyboard shortcut: Space to spin
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.code === 'Space' && canSpin && !showResult && !showHistory) {
        e.preventDefault()
        handleSpin()
      }
      if (e.code === 'Escape') {
        setShowResult(false)
        setShowHistory(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  })

  // Session timeout
  useEffect(() => {
    const check = setInterval(() => {
      const last = useStore.getState().lastActivity
      if (Date.now() - last > SESSION_TIMEOUT && useStore.getState().user) {
        store.logout()
        showToast('warning', 'Session expired. Please log in again.')
      }
    }, 30_000)
    return () => clearInterval(check)
  }, [store])

  // Auto-refresh balance
  useEffect(() => {
    if (!user) return
    const refresh = setInterval(async () => {
      try {
        const data = await getBalance(user.id)
        if (data.balance !== undefined) store.setBalance(data.balance)
      } catch { /* silent */ }
    }, BALANCE_REFRESH_INTERVAL)
    return () => clearInterval(refresh)
  }, [user, store])

  // Fetch eligibility data
  useEffect(() => {
    if (!user) return
    const fetchEligibility = async () => {
      try {
        const data = await getEligibility(user.id)
        setEligibility(data)
      } catch { /* silent - eligibility is optional */ }
    }
    fetchEligibility()
    // Refresh after each spin
  }, [user, spins.length])

  // Refresh balance on tab focus
  useEffect(() => {
    if (!user) return
    const handler = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const data = await getBalance(user.id)
          if (data.balance !== undefined) store.setBalance(data.balance)
        } catch { /* silent */ }
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [user, store])

  // Update page title with balance
  useEffect(() => {
    document.title = user
      ? `${user.balance.toLocaleString()} pts | Perceptrons Prize Wheel`
      : 'Perceptrons Prize Wheel'
  }, [user?.balance, user])

  // Pull-to-refresh (mobile)
  useEffect(() => {
    if (!user) return
    const onTouchStart = (e: TouchEvent) => { pullStartY.current = e.touches[0].clientY }
    const onTouchMove = (e: TouchEvent) => {
      pullDelta.current = e.touches[0].clientY - pullStartY.current
    }
    const onTouchEnd = async () => {
      if (pullDelta.current > 80 && window.scrollY === 0 && !refreshing) {
        setRefreshing(true)
        try {
          const data = await getBalance(user.id)
          if (data.balance !== undefined) store.setBalance(data.balance)
          showToast('info', 'Balance refreshed')
        } catch { /* silent */ }
        setRefreshing(false)
      }
      pullDelta.current = 0
    }
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    window.addEventListener('touchend', onTouchEnd)
    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
    }
  }, [user, store, refreshing])

  const handleSpin = async () => {
    if (!canSpin || !user) return
    // CRITICAL: Clear previous result BEFORE setting spinning=true
    // This prevents the wheel from animating to the OLD targetIndex
    store.setLastSpinResult(null)
    store.setSpinning(true)
    store.touch()
    setShowResult(false)
    if (soundEnabled) playSpinStart()

    if (testMode) {
      // Test mode: generate result locally, no DB writes
      const segIdx = pickTestSegment()
      const prizeType = PRIZE_TYPE_MAP[segIdx]
      const pointsAfter = prizeType === 'free_spin' ? user.balance : user.balance - SPIN_COST
      const fakeResult: SpinResult = {
        id: `test-${Date.now()}`,
        user_id: user.id,
        prize_type: prizeType,
        points_spent: prizeType === 'free_spin' ? 0 : SPIN_COST,
        points_before: user.balance,
        points_after: pointsAfter,
        segment_index: segIdx,
        seed: Math.random(),
        created_at: new Date().toISOString(),
      }
      store.addSpin(fakeResult)
      store.setLastSpinResult(fakeResult)
      return
    }

    try {
      const r = await spinWheel(user.id, SPIN_COST)
      store.addSpin(r)
      store.setLastSpinResult(r)
    } catch (err) {
      store.setSpinning(false)
      showToast('error', sanitizeErrorMessage(err))
    }
  }

  const handleSpinComplete = useCallback(() => {
    const state = useStore.getState()
    state.setSpinning(false)
    setShowResult(true)
    const result = state.lastSpinResult
    if (!result) return
    if (isWin(result.prize_type)) {
      if (state.soundEnabled) playWin()
      fireConfetti()
      setScreenFlash(true)
      setTimeout(() => setScreenFlash(false), 500)
    } else {
      if (state.soundEnabled) playLose()
    }
  }, [])

  const handleShare = () => {
    if (!lastSpinResult || !isWin(lastSpinResult.prize_type)) return
    const text = `I just won ${prizeLabel(lastSpinResult.prize_type)} on @PerceptronNet Prize Wheel! Spin yours at`
    const url = 'https://prize-wheel-main.vercel.app'
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank')
  }

  const totalSpent = spins.reduce((s, sp) => s + sp.points_spent, 0)
  const winCount = spins.filter(s => isWin(s.prize_type)).length

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <ParticleBackground />

      {/* Screen flash on win */}
      {screenFlash && (
        <div className="fixed inset-0 z-[60] pointer-events-none screen-flash"
          style={{ background: 'var(--brand-primary)' }} />
      )}

      {/* ── HEADER ── */}
      <header className="relative z-20 px-4 sm:px-6 py-2 flex items-center justify-between"
        style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-subtle)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
        <div className="flex items-center gap-3">
          <img src={theme === 'dark' ? '/LOGO-dark.png' : '/LOGO-light.png'} alt="Perceptrons" className="h-10 sm:h-12 w-auto" />
          <div className="leading-tight">
            <div className="text-sm sm:text-base font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}><span className="text-brand">Perceptron</span> Network</div>
            <div className="text-[10px] sm:text-xs" style={{ color: 'var(--text-muted)' }}>Prize Wheel</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {testMode && (
            <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider mr-1"
              style={{ background: 'rgba(245,158,11,0.12)', color: '#D97706', border: '1px solid rgba(245,158,11,0.25)' }}>
              Test
            </span>
          )}
          <button onClick={() => { store.toggleTheme(); if (soundEnabled) playClick() }}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            className="p-2 rounded-lg hover-surface"
            style={{ color: 'var(--text-muted)' }}>
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={() => { store.toggleSound(); playClick() }}
            aria-label={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
            className="p-2 rounded-lg hover-surface"
            style={{ color: 'var(--text-muted)' }}>
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button onClick={() => { setShowHistory(true); if (soundEnabled) playClick() }}
            aria-label="Spin history"
            className="p-2 rounded-lg hover-surface"
            style={{ color: 'var(--text-muted)' }}>
            <History className="w-4 h-4" />
          </button>
          <span className="text-[11px] hidden sm:inline truncate max-w-[150px] ml-1"
            style={{ color: 'var(--text-muted)' }}>{user?.email}</span>
          <button onClick={() => setShowLogoutConfirm(true)}
            aria-label="Logout"
            className="p-2 rounded-lg hover-surface"
            style={{ color: 'var(--text-muted)' }}>
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main className="relative z-10 flex-1 flex flex-col items-center px-4 sm:px-6 pt-2 pb-3 overflow-y-auto">
        {/* Pull-to-refresh indicator */}
        {refreshing && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 py-1"
          >
            <RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--brand-primary)' }} />
            <span className="text-xs" style={{ color: 'var(--brand-primary)' }}>Refreshing...</span>
          </motion.div>
        )}

        {/* Admin toggle */}
        {isAdmin && (
          <button
            onClick={() => { setTestMode(prev => !prev); showToast('info', testMode ? 'LIVE MODE — spins now save to database' : 'TEST MODE — spins are local only') }}
            className="mb-1 px-3 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all"
            style={{
              background: testMode ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)',
              color: testMode ? '#DC2626' : '#16A34A',
              border: `1px solid ${testMode ? 'rgba(220,38,38,0.2)' : 'rgba(22,163,74,0.2)'}`,
            }}
          >
            {testMode ? '⚠ Go Live' : '✓ Live — Switch to Test'}
          </button>
        )}

        {/* Balance hero */}
        <motion.div
          className="text-center mb-1"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-xs uppercase tracking-[0.15em] mb-0.5" style={{ color: 'var(--text-muted)' }}>
            Your Balance
          </div>
          <div className="text-3xl sm:text-4xl font-bold tabular-nums leading-tight" style={{ color: 'var(--text-primary)' }}>
            {animatedBalance.toLocaleString()}
          </div>
          <div className="text-xs mt-0.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>points · {SPIN_COST.toLocaleString()} per spin</div>
        </motion.div>

        {/* Wheel */}
        <motion.div
          className="w-full max-w-[600px] -mt-1"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
        >
          <PrizeWheel
            spinning={isSpinning}
            targetIndex={lastSpinResult?.segment_index}
            onSpin={handleSpin}
            onSpinComplete={handleSpinComplete}
            disabled={!canSpin}
          />
        </motion.div>

        {user && user.balance < SPIN_COST && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm font-medium mt-1" style={{ color: 'var(--danger)' }}>Insufficient points</motion.p>
        )}

        {/* Disclaimer */}
        <p className="mt-1 max-w-sm text-center text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)', opacity: 0.7 }}>
          Each spin deducts {SPIN_COST.toLocaleString()} pts. This will be reflected at launch.
          {testMode && <span className="font-semibold" style={{ color: '#D97706' }}> Test mode — points not saved.</span>}
        </p>

        {/* Stats row */}
        <motion.div
          className="flex gap-2 sm:gap-3 mt-3 flex-wrap justify-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Pill label="Spins" value={spins.length} icon={<Zap className="w-4 h-4" />} />
          <Pill label="Spent" value={totalSpent.toLocaleString()} icon={<Sparkles className="w-4 h-4" />} />
          <Pill label="Wins" value={winCount} icon={<Trophy className="w-4 h-4" />} />
          {winStreak > 1 && (
            <Pill label="Streak" value={winStreak} icon={<Flame className="w-4 h-4" />} highlight />
          )}
        </motion.div>

        {/* Recent spins quick view */}
        {spins.length > 0 && (
          <motion.div
            className="mt-4 w-full max-w-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="text-[11px] uppercase tracking-[0.15em] mb-2 text-center font-semibold"
              style={{ color: 'var(--text-muted)' }}>
              Recent Spins
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {spins.slice(0, 12).map((s, i) => (
                <motion.span
                  key={s.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.04 }}
                  className="px-3 py-1.5 rounded-lg text-[12px] font-semibold"
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    ...(isWin(s.prize_type) ? { color: 'var(--success)', borderColor: 'rgba(22,163,74,0.25)' } : { color: 'var(--text-muted)' }),
                  }}
                >
                  {prizeLabel(s.prize_type)}
                </motion.span>
              ))}
            </div>
            {spins.length > 12 && (
              <button onClick={() => setShowHistory(true)}
                className="mt-2 text-[11px] font-semibold mx-auto block"
                style={{ color: 'var(--brand-primary)' }}>
                View all {spins.length} spins &rarr;
              </button>
            )}
          </motion.div>
        )}

        {/* Achievement badges */}
        {spins.length > 0 && (
          <motion.div
            className="mt-4 w-full max-w-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <div className="text-[11px] uppercase tracking-[0.15em] mb-2 text-center font-semibold"
              style={{ color: 'var(--text-muted)' }}>
              Achievements
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                { id: 'first', label: 'First Spin', icon: Zap, unlocked: spins.length >= 1 },
                { id: '10spins', label: '10 Spins', icon: Sparkles, unlocked: spins.length >= 10 },
                { id: 'winner', label: 'First Win', icon: Trophy, unlocked: spins.some(s => isWin(s.prize_type)) },
                { id: 'streak3', label: '3x Streak', icon: Flame, unlocked: (() => { let max = 0, cur = 0; for (const s of spins) { if (isWin(s.prize_type)) { cur++; max = Math.max(max, cur) } else cur = 0 } return max >= 3 })() },
                { id: '50spins', label: '50 Spins', icon: Award, unlocked: spins.length >= 50 },
              ].map((a) => (
                <motion.div
                  key={a.id}
                  whileHover={{ scale: a.unlocked ? 1.08 : 1 }}
                  className="flex flex-col items-center gap-1 p-2.5 rounded-xl w-[72px]"
                  style={{
                    opacity: a.unlocked ? 1 : 0.3,
                    background: a.unlocked ? 'var(--bg-card)' : 'transparent',
                    border: a.unlocked ? '1px solid var(--border-subtle)' : '1px solid transparent',
                  }}
                  title={a.unlocked ? `${a.label} - Unlocked!` : `${a.label} - Locked`}
                >
                  <a.icon className="w-5 h-5" style={{ color: a.unlocked ? 'var(--brand-primary)' : 'var(--text-muted)' }} />
                  <span className="text-[9px] font-bold text-center leading-tight" style={{ color: a.unlocked ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {a.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Info cards row */}
        <motion.div
          className="mt-4 w-full max-w-md grid grid-cols-3 gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="rounded-xl p-3 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
            <Gift className="w-5 h-5 mx-auto mb-1.5" style={{ color: 'var(--success)' }} />
            <div className="text-[11px] font-semibold" style={{ color: 'var(--text-primary)' }}>Daily Bonus</div>
            <span className="inline-block text-[10px] font-bold mt-0.5 px-2 py-0.5 rounded-full" style={{ background: 'rgba(22,163,74,0.08)', color: 'var(--success)' }}>Active</span>
          </div>

          <div className="rounded-xl p-3 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
            <Zap className="w-5 h-5 mx-auto mb-1.5" style={{ color: 'var(--brand-primary)' }} />
            <div className="text-[11px] font-semibold" style={{ color: 'var(--text-primary)' }}>Progress</div>
            {eligibility ? (
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {eligibility.total_spins} spins
              </div>
            ) : (
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Loading...</div>
            )}
          </div>

          <div className="rounded-xl p-3 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', opacity: 0.6 }}>
            <Users className="w-5 h-5 mx-auto mb-1.5" style={{ color: 'var(--text-muted)' }} />
            <div className="text-[11px] font-semibold" style={{ color: 'var(--text-primary)' }}>Leaderboard</div>
            <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Coming Soon</div>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="mt-auto pt-4 pb-1 text-center">
          <ExternalLinks variant="compact" />
          <div className="flex items-center justify-center gap-4 text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
            <a href="https://perceptrons.xyz" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:underline transition-colors"
              style={{ color: 'var(--brand-primary)' }}>
              perceptrons.xyz <ExternalLink className="w-3 h-3" />
            </a>
            <span>&middot;</span>
            <span>v1.0</span>
          </div>
        </div>
      </main>

      {/* ── RESULT MODAL ── */}
      <AnimatePresence>
        {showResult && lastSpinResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)' }}
            onClick={() => setShowResult(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Spin result"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="mx-4 w-full max-w-sm"
              onClick={e => e.stopPropagation()}
            >
              <div className="rounded-2xl p-8 text-center shadow-2xl" style={{ background: 'var(--bg-card)', border: '2px solid var(--border-strong)' }}>
                {/* Prize emoji area */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.1, type: 'spring', damping: 12 }}
                  className="text-5xl mb-3"
                >
                  {prizeEmoji(lastSpinResult.prize_type) || (isWin(lastSpinResult.prize_type) ? '' : '')}
                </motion.div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className={`text-2xl sm:text-3xl font-black mb-1 ${
                    isWin(lastSpinResult.prize_type) ? 'text-gradient-brand' : ''
                  }`}
                  style={!isWin(lastSpinResult.prize_type) ? { color: 'var(--text-secondary)' } : {}}>
                    {prizeLabel(lastSpinResult.prize_type)}
                  </div>
                  <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                    {isWin(lastSpinResult.prize_type) ? 'Congratulations!' : 'Better luck next time!'}
                  </p>
                  <p className="text-[11px] mb-6" style={{ color: 'var(--text-muted)' }}>
                    Balance: {lastSpinResult.points_after.toLocaleString()} pts
                  </p>
                </motion.div>

                <div className="flex gap-2 justify-center">
                  <button onClick={() => setShowResult(false)}
                    className="px-8 py-2.5 rounded-full font-bold text-sm
                      btn-brand
                      active:scale-95 transition-all">
                    {isWin(lastSpinResult.prize_type) ? 'Claim & Continue' : 'Spin Again'}
                  </button>
                  {isWin(lastSpinResult.prize_type) && (
                    <button onClick={handleShare}
                      aria-label="Share on Twitter"
                      className="px-3 py-2.5 rounded-full btn-outline transition-all hover:scale-105">
                      <Share2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HISTORY PANEL ── */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowHistory(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Spin history"
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-lg max-h-[80vh] overflow-y-auto mx-4 mb-4 sm:mb-0"
              onClick={e => e.stopPropagation()}
            >
              <div className="rounded-2xl p-6" style={{ background: 'var(--bg-card)', border: '2px solid var(--border-strong)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-black" style={{ color: 'var(--text-primary)' }}>Spin <span className="text-brand">History</span></h2>
                  <button onClick={() => setShowHistory(false)}
                    className="p-1 rounded-lg transition-colors"
                    style={{ color: 'var(--text-muted)' }}
                    aria-label="Close history panel">
                    &times;
                  </button>
                </div>

                {spins.length === 0 ? (
                  <p className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>
                    No spins yet. Try your luck!
                  </p>
                ) : (
                  <div className="space-y-2">
                    {spins.map((s, i) => (
                      <motion.div
                        key={s.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="flex items-center justify-between p-3 rounded-xl"
                        style={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border-subtle)',
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm"
                            style={{
                              background: isWin(s.prize_type) ? 'rgba(16,185,129,0.1)' : 'var(--bg-card)',
                              color: isWin(s.prize_type) ? 'var(--success)' : 'var(--text-muted)',
                            }}>
                            {isWin(s.prize_type) ? <Trophy className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                          </div>
                          <div>
                            <div className="text-xs font-bold" style={{
                              color: isWin(s.prize_type) ? 'var(--success)' : 'var(--text-primary)',
                            }}>
                              {prizeLabel(s.prize_type)}
                            </div>
                            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                              {new Date(s.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] font-bold" style={{ color: 'var(--danger)' }}>
                            -{s.points_spent.toLocaleString()}
                          </div>
                          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            {s.points_after.toLocaleString()} left
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── LOGOUT CONFIRM ── */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowLogoutConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="rounded-2xl p-6 mx-4 w-full max-w-xs text-center"
              style={{ background: 'var(--bg-card)', border: '2px solid var(--border-strong)' }}
              onClick={e => e.stopPropagation()}
            >
              <LogOut className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-secondary)' }} />
              <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Logout?</h3>
              <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>Your spin history will be cleared from this device.</p>
              <div className="flex gap-2">
                <button onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 py-2 rounded-full text-xs font-bold btn-outline transition-all hover:scale-[1.02]">
                  Cancel
                </button>
                <button onClick={() => { store.logout(); setShowLogoutConfirm(false); showToast('info', 'Logged out') }}
                  className="flex-1 py-2 rounded-full text-xs font-bold btn-brand transition-all hover:scale-[1.02]">
                  Logout
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Screen reader announcements */}
      <div aria-live="polite" className="sr-only">
        {showResult && lastSpinResult && (
          <p>Spin result: {prizeLabel(lastSpinResult.prize_type)}.
            {isWin(lastSpinResult.prize_type) ? ' You won!' : ' Better luck next time.'}
          </p>
        )}
      </div>
    </div>
  )
}

function Pill({ label, value, icon, highlight }: { label: string; value: string | number; icon?: React.ReactNode; highlight?: boolean }) {
  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      className="flex items-center gap-2 px-4 py-2 rounded-xl cursor-default"
      style={{
        background: highlight ? 'rgba(255,107,0,0.05)' : 'var(--bg-card)',
        border: highlight ? '1px solid rgba(255,107,0,0.2)' : '1px solid var(--border-subtle)',
      }}
    >
      {icon && <span style={{ color: highlight ? '#FF6B00' : 'var(--text-muted)' }}>{icon}</span>}
      <span className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="text-sm font-bold tabular-nums" style={{ color: highlight ? '#FF6B00' : 'var(--text-primary)' }}>{value}</span>
    </motion.div>
  )
}

export default function App() {
  const user = useStore(s => s.user)
  const [appReady, setAppReady] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setAppReady(true), 800)
    return () => clearTimeout(timer)
  }, [])

  if (!appReady) return <LoadingScreen />

  return (
    <ErrorBoundary>
      <ToastContainer />
      <ConnectionIndicator />
      <CursorGlow />
      <AnimatePresence mode="wait">
        {user ? (
          <motion.div
            key="wheel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <WheelPage />
          </motion.div>
        ) : (
          <motion.div
            key="login"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <LoginPage />
          </motion.div>
        )}
      </AnimatePresence>
    </ErrorBoundary>
  )
}
